import {
  Dialog,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  IconButton,
} from '@mui/material';
import {
  Close, ListAlt,
} from '@mui/icons-material';
import LinkIcon from '@mui/icons-material/Link';
import { useState } from 'react';
import { useModal } from 'mui-modal-provider';
import { NetworkIcon } from 'elements/NetworkIcon';
import { NETWORK_IDS, NETWORKS } from 'lib/constants';
import { userIsProOrAdminOrOG, userIsAdmin, userHasTrialSubscription } from 'lib/helpers';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { useSnackbar } from 'notistack';
import { ProBadge } from 'components/ProBadge';
import { useUpgradeModal } from 'components/UpgradeModal';
import { ConfirmationModal } from 'components/ConfirmationModal';
import { User } from 'lib/types/users';
import { SOURCE_IDS } from '../constants';

interface Props {
  onClose: () => void;
  onSelect: (source: number) => void;
}

export const SelectSource = ({ onClose, onSelect }: Props) => {
  const currentUser = useCurrentUser();
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const { showModal } = useModal();

  const [isTrialing, setIsTrialing] = useState(userHasTrialSubscription(currentUser as User));
  const [isLoading, setIsLoading] = useState(false);
  const { showUpgradeModal } = useUpgradeModal();

  const clickEndTrialEarly = async () => {
    const modal = showModal(ConfirmationModal, {
      text: (
        <>
          This will immediately end your PRO trial and begin your active PRO subscription.
          You will be charged
          {' '}
          <span className="font-bold">$9.99.</span>
          <br />
          Would you like to continue?
        </>
      ),
      onNo: () => {
        modal.destroy();
      },
      onYes: async () => {
        try {
          setIsLoading(true);
          await API.endTrialEarly();
          enqueueSnackbar('Your subscription is now active - thank you for being a Gondola PRO!', { variant: 'success' });
          setIsTrialing(false);
        } catch (err) {
          enqueueSnackbar('We were not able to make your subscription active. Please contact support@gondola.cc for help!', { variant: 'error' });
        }
        setIsLoading(false);
        modal.destroy();
      },
    });
  };

  const selectSource = (sourceId: number) => {
    if (userIsProOrAdminOrOG(currentUser)) {
      onSelect(sourceId);
    } else {
      showUpgradeModal(`Upgrade to bulk import posts ${sourceId === SOURCE_IDS.listOfUrls ? '' : `from ${NETWORKS[sourceId].title}`}`);
    }
  };

  return (
    <Dialog fullWidth onClose={onClose} aria-labelledby="title" open>
      <div className="absolute right-2 top-2">
        <IconButton aria-label="close" onClick={onClose}>
          <Close />
        </IconButton>
      </div>
      <div className="p-4">
        <h1 className="text-2xl font-semibold px-4">
          Add posts to Gondola
        </h1>
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => onSelect(SOURCE_IDS.singleLink)}
            >
              <ListItemIcon>
                <LinkIcon />
              </ListItemIcon>
              <ListItemText primary="Any social link(s)" />
            </ListItemButton>
          </ListItem>
          <h2 className="text-xl font-semibold ml-4 mt-2">
            Bulk import posts from
            {' '}
            <span className="italic">any</span>
            {' '}
            account
          </h2>
          <ListItem disablePadding>
            <ListItemButton
              disabled={isTrialing}
              onClick={() => selectSource(SOURCE_IDS.instagram)}
            >
              <ListItemIcon>
                <NetworkIcon network={NETWORK_IDS.instagram} iconColor="color" />
              </ListItemIcon>
              <ListItemText primary="Instagram" />
              <ProBadge />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              disabled={isTrialing}
              onClick={() => selectSource(SOURCE_IDS.twitter)}
            >
              <ListItemIcon>
                <NetworkIcon network={NETWORK_IDS.twitter} iconColor="color" />
              </ListItemIcon>
              <ListItemText primary="Twitter" />
              <ProBadge />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              disabled={isTrialing}
              onClick={() => selectSource(SOURCE_IDS.tiktok)}
            >
              <ListItemIcon>
                <NetworkIcon network={NETWORK_IDS.tiktok} iconColor="color" />
              </ListItemIcon>
              <ListItemText primary="TikTok" />
              <ProBadge />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              disabled={isTrialing}
              onClick={() => selectSource(SOURCE_IDS.youtube)}
            >
              <ListItemIcon>
                <NetworkIcon network={NETWORK_IDS.youtube} iconColor="color" />
              </ListItemIcon>
              <ListItemText primary="YouTube" />
              <ProBadge />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              disabled={isTrialing}
              onClick={() => selectSource(SOURCE_IDS.twitch)}
            >
              <ListItemIcon>
                <NetworkIcon network={NETWORK_IDS.twitch} iconColor="color" />
              </ListItemIcon>
              <ListItemText primary="Twitch" />
              <ProBadge />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              disabled={isTrialing}
              onClick={() => selectSource(SOURCE_IDS.threads)}
            >
              <ListItemIcon>
                <NetworkIcon network={NETWORK_IDS.threads} iconColor="color" />
              </ListItemIcon>
              <ListItemText primary="Threads" />
              <ProBadge />
            </ListItemButton>
          </ListItem>
          {userIsAdmin(currentUser) && (
            <ListItem disablePadding>
              <ListItemButton
                disabled={isTrialing}
                onClick={() => selectSource(SOURCE_IDS.linkedin)}
              >
                <ListItemIcon>
                  <NetworkIcon network={NETWORK_IDS.linkedin} iconColor="color" />
                </ListItemIcon>
                <ListItemText primary="LinkedIn (admin only)" />
                <ProBadge />
              </ListItemButton>
            </ListItem>
          )}
          <ListItem disablePadding>
            <ListItemButton
              disabled={isTrialing || !currentUser?.username}
              onClick={() => selectSource(SOURCE_IDS.listOfUrls)}
            >
              <ListItemIcon>
                <ListAlt />
              </ListItemIcon>
              <ListItemText primary="List of links" />
              <ProBadge />
            </ListItemButton>
          </ListItem>
        </List>
        {
          isTrialing
          && (
            <div className="p-4">
              <div>Hey trialing PRO user 👋!</div>
              <div className="mb-4">
                We reserve bulk importing exclusively for Gondola PROs
                who have completed their trial phase.
              </div>
              <div className="flex items-center gap-2">
                <div>
                  <span className="font-bold">End your trial period early and activate PRO subscription now</span>
                  {' '}
                  to access this time-saving feature!
                </div>
                <button
                  disabled={isLoading}
                  type="button"
                  className="btn-primary"
                  onClick={clickEndTrialEarly}
                >
                  Activate
                </button>
              </div>
            </div>
          )
        }
      </div>
    </Dialog>
  );
};
